import api from './api';
import type { User, UserFormData, HydraCollection } from '../types/entities';

const BASE_PATH = '/api/users';

export interface UserFilters {
  company?: string | null; // company id
  role?: string | null; // ROLE_* value
  plant?: string | null; // plant id
  category?: string | null; // report category value
}

function buildQueryParams(filters?: UserFilters): string {
  if (!filters) return '';

  const params = new URLSearchParams();
  if (filters.company) params.append('company', `/api/companies/${filters.company}`);
  if (filters.plant) params.append('plants', `/api/plants/${filters.plant}`);
  if (filters.role) params.append('roles', filters.role);
  if (filters.category) params.append('enabledReportCategories', filters.category);

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

export const usersApi = {
  getAll: async (filters?: UserFilters): Promise<User[]> => {
    const response = await api.get<HydraCollection<User> | User[]>(
      `${BASE_PATH}${buildQueryParams(filters)}`,
    );
    // Handle both JSON-LD (hydra) and plain JSON formats
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data['hydra:member'] ?? [];
  },

  getOne: async (id: number): Promise<User> => {
    const response = await api.get<User>(`${BASE_PATH}/${id}`);
    return response.data;
  },

  create: async (data: UserFormData): Promise<User> => {
    const response = await api.post<User>(BASE_PATH, data);
    return response.data;
  },

  update: async (id: number, data: Partial<UserFormData>): Promise<User> => {
    const response = await api.patch<User>(`${BASE_PATH}/${id}`, data, {
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`${BASE_PATH}/${id}`);
  },
};
